import type { PosSession, PosTerminal, TerminalShift, TerminalShiftSale } from '@erp/shared';
import { HandCoins, Printer, Undo2, Wallet } from 'lucide-react';
import { useEffect, useMemo, useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

import {
  Badge,
  Button,
  Card,
  DataTable,
  type DataTableColumn,
  EmptyState,
  Skeleton,
} from '../../components/ui/index.js';
import { api } from '../../lib/api.js';
import { useAuthStore } from '../../lib/authStore.js';
import { formatDateTime, formatNumber } from '../../lib/intl.js';
import { formatMoney } from '../../lib/money.js';
import { listUsers, type UserListItem } from '../../lib/users.js';

import { SessionSalesDialog } from './SessionSalesDialog.js';
import {
  type CurrencyBreakdown, sessionBreakdown, sumBreakdowns,
  tillBreakdown, tillDrawerCards, tillSaleRows,
} from './shiftBreakdown.js';
import { printSessionCloseout } from './shiftPrint.js';
import { buildShiftPrintHtml, methodLabel, printHtml } from './shiftReport.js';
import { useShiftMoney } from './ShiftView.js';
import { TerminalShiftDialog } from './TerminalShiftDialog.js';

/** Where a close-out came from: the offline .exe till, or the browser register. */
type ShiftSource = 'terminal' | 'register';

/** One currency's drawer reconciliation, as the table renders it. */
interface DrawerLine {
  currencyCode: string;
  expected: string;
  /** null while the shift is still open — nothing has been counted yet. */
  counted: string | null;
  variance: string | null;
}

/**
 * A close-out from either source, reduced to the columns the office reads. The
 * two backing tables record the same shift in different shapes, and an
 * accountant asking "which drawers came up short this week" does not care which
 * program the cashier used.
 */
/**
 * An ISO timestamp as its LOCAL calendar day (YYYY-MM-DD).
 *
 * `.slice(0, 10)` on the raw string would give the UTC day, which disagrees with
 * the date this page prints beside the filter for every shift closed after
 * 21:00 in Baghdad.
 */
function localDay(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso.slice(0, 10);
  const m = `${d.getMonth() + 1}`.padStart(2, '0');
  const day = `${d.getDate()}`.padStart(2, '0');
  return `${d.getFullYear()}-${m}-${day}`;
}

interface ShiftRow {
  key: string;
  source: ShiftSource;
  isOpen: boolean;
  openedAt: string;
  closedAt: string | null;
  deviceName: string;
  cashierName: string;
  salesCount: number | null;
  takings: Array<{ currencyCode: string; total: string }>;
  /** How it was paid, per currency — cash, card, transfer, on account…, returns, debt payments. */
  breakdown: CurrencyBreakdown[];
  drawer: DrawerLine[];
  /** The till slip behind this row — only a terminal close-out has one to open. */
  till: TerminalShift | null;
  /** The register session behind this row — its dialog lists the sales instead. */
  session: PosSession | null;
  /**
   * The branch, through the terminal's pin. Null for .exe till slips — the
   * device slip names no terminal, so those rows count as unbranched.
   */
  warehouseId: string | null;
}

function fromTill(s: TerminalShift): ShiftRow {
  return {
    key: `terminal:${s.id}`,
    source: 'terminal',
    isOpen: false,
    openedAt: s.openedAt,
    closedAt: s.closedAt,
    deviceName: s.deviceName ?? '',
    cashierName: s.openedByName ?? '',
    salesCount: s.salesCount,
    takings: [
      { currencyCode: s.currencyCode, total: s.totalTaken },
      ...s.foreignCash.filter((f) => Number(f.totalTaken) !== 0).map((f) => ({ currencyCode: f.currencyCode, total: f.totalTaken })),
    ],
    breakdown: tillBreakdown(s),
    drawer: [{
      currencyCode: s.currencyCode,
      expected: s.expectedCash,
      counted: s.countedCash,
      variance: s.difference,
    }],
    till: s,
    session: null,
    warehouseId: null,
  };
}

/**
 * A browser-register shift. `drawerCounts` is the per-currency truth; shifts
 * closed before it existed fall back to the three scalar columns, which are the
 * session currency's slice of the same numbers. An OPEN shift has no drawer at
 * all — expected cash is only computed at close, and showing a zero there would
 * read as a balanced count that never happened.
 */
function fromSession(s: PosSession, deviceName: string, cashierName: string, warehouseId: string | null): ShiftRow {
  const closed = s.status !== 'open';
  const drawer: DrawerLine[] = !closed
    ? []
    : s.drawerCounts?.map((d) => ({
      currencyCode: d.currencyCode,
      expected: d.expected,
      counted: d.counted,
      variance: d.variance,
    })) ?? [{
      currencyCode: s.currencyCode,
      expected: s.expectedCash ?? '0',
      counted: s.closingCash,
      variance: s.variance,
    }];
  return {
    key: `register:${s.id}`,
    breakdown: sessionBreakdown(s),
    source: 'register',
    isOpen: !closed,
    openedAt: s.openedAt,
    closedAt: s.closedAt,
    deviceName,
    cashierName,
    salesCount: s.salesCount ?? null,
    takings: s.takings ?? [],
    drawer,
    till: null,
    session: s,
    warehouseId,
  };
}

/**
 * One line per currency. A drawer can hold IQD and USD at the same time and this
 * codebase never adds them into a single figure, so a mixed-currency cell grows
 * a row instead of a total.
 */
function perCurrency(lines: Array<{ currencyCode: string; node: ReactNode }>): ReactNode {
  if (lines.length === 0) return '—';
  if (lines.length === 1) return lines[0]?.node;
  return (
    <div className="flex flex-col gap-0.5">
      {lines.map((l) => (
        <div key={l.currencyCode}>{l.node}</div>
      ))}
    </div>
  );
}

/**
 * Register close-outs, from BOTH tills: the offline .exe terminals that push a
 * signed slip up on sync, and the browser register whose cashier closes a shift
 * against a counted drawer. Those live in different tables and only the first
 * used to reach this page, so half the shop's cash reconciliation was invisible
 * to the office.
 *
 * An audit list, not a live view. Nothing here is editable — the point is that
 * the office sees the same numbers the cashier signed for.
 *
 * The DIFFERENCE is the reason this page exists, so it is the thing the eye
 * lands on: coloured, signed, and sortable, because "which tills came up short
 * this week" is the actual question being asked. The filters above the table
 * answer the accountant's other questions (which day, which register, whose
 * shift) and the money columns are always per currency, never one total.
 */
export function TerminalShiftsPage(): JSX.Element {
  const { t, i18n } = useTranslation('pos');
  const { t: tc } = useTranslation();
  const tenant = useAuthStore((st) => st.tenant);
  const [tills, setTills] = useState<TerminalShift[] | null>(null);
  const [sessions, setSessions] = useState<PosSession[] | null>(null);
  const [terminals, setTerminals] = useState<PosTerminal[]>([]);
  const [users, setUsers] = useState<UserListItem[]>([]);
  // The row itself, not an id: the dialog's figures ARE this row (what the till
  // counted and printed), so there is nothing to re-fetch and nothing to go stale.
  const [open, setOpen] = useState<TerminalShift | null>(null);
  const [openSession, setOpenSession] = useState<PosSession | null>(null);

  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');
  const [deviceFilter, setDeviceFilter] = useState('all');
  const [cashierFilter, setCashierFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [sourceFilter, setSourceFilter] = useState('all');
  const [branchFilter, setBranchFilter] = useState('all');
  const [warehouses, setWarehouses] = useState<Array<{ id: string; name: string }>>([]);
  const [varianceOnly, setVarianceOnly] = useState(false);

  // Each half is loaded best-effort: a shop with no .exe tills, or an accountant
  // whose role can read sessions but not the sync tables, still gets the other list.
  useEffect(() => {
    void api()
      .get<{ items: TerminalShift[] }>('/pos/terminal-shifts', { params: { limit: 500 } })
      .then((r) => setTills(r.data.items))
      .catch(() => setTills([]));
    void api()
      .get<{ items: PosSession[] }>('/pos/sessions', { params: { limit: 500 } })
      .then((r) => setSessions(r.data.items))
      .catch(() => setSessions([]));
  }, []);

  // Directories for the register shifts' terminal + cashier names (the till slips
  // carry their own). Best-effort — a lookup miss shows "—", it never blocks the page.
  useEffect(() => {
    void (async () => {
      try {
        const r = await api().get<{ items: PosTerminal[] }>('/pos/terminals');
        setTerminals(r.data.items);
      } catch { /* non-fatal */ }
    })();
    void (async () => {
      try {
        setUsers(await listUsers());
      } catch { /* non-fatal */ }
    })();
    void (async () => {
      try {
        const r = await api().get<{ items: Array<{ id: string; name: string }> }>('/warehouses');
        setWarehouses(r.data.items);
      } catch { /* non-fatal */ }
    })();
  }, []);

  const terminalById = useMemo(() => new Map(terminals.map((tm) => [tm.id, tm.name])), [terminals]);
  const terminalBranch = useMemo(() => new Map(terminals.map((tm) => [tm.id, tm.warehouseId])), [terminals]);
  const userById = useMemo(() => new Map(users.map((u) => [u.id, u.fullName])), [users]);
  const shiftMoney = useShiftMoney();

  const rows = useMemo<ShiftRow[] | null>(() => {
    if (!tills || !sessions) return null;
    return [
      ...tills.map(fromTill),
      ...sessions.map((s) =>
        fromSession(s, terminalById.get(s.terminalId) ?? '', userById.get(s.cashierUserId) ?? '', terminalBranch.get(s.terminalId) ?? null),
      ),
    ];
  }, [tills, sessions, terminalById, terminalBranch, userById]);

  if (!rows) return <Card className="p-6"><Skeleton className="h-32 w-full" /></Card>;

  const money = (v: string, currency: string): string => formatMoney(v, { locale: i18n.language, currency });

  const devices = [...new Set(rows.map((r) => r.deviceName).filter(Boolean))].sort();
  const cashiers = [...new Set(rows.map((r) => r.cashierName).filter(Boolean))].sort();
  const anyFilter = fromDate !== '' || toDate !== '' || deviceFilter !== 'all'
    || cashierFilter !== 'all' || statusFilter !== 'all' || sourceFilter !== 'all'
    || branchFilter !== 'all' || varianceOnly;
  const clearFilters = (): void => {
    setFromDate('');
    setToDate('');
    setDeviceFilter('all');
    setCashierFilter('all');
    setStatusFilter('all');
    setSourceFilter('all');
    setBranchFilter('all');
    setVarianceOnly(false);
  };

  /**
   * The printed close-out for any row — a till slip or a register shift — with
   * everything the dialog shows, loaded here so the manager reconciling a stack
   * of shifts never has to open each one first.
   */
  const printRow = async (s: ShiftRow): Promise<void> => {
    const base = s.till?.currencyCode ?? s.session?.currencyCode ?? '';
    const ratesP = api()
      .get<{ items: Array<{ code: string; rate: string }> }>('/currencies/display-rates', { params: { base } })
      .then((r) => Object.fromEntries(r.data.items.map((x) => [x.code, Number(x.rate)])) as Record<string, number>)
      .catch(() => ({} as Record<string, number>));
    const opts = (rates: Record<string, number>) => ({
      storeName: tenant?.name ?? '', locale: i18n.language, rtl: i18n.dir() === 'rtl', rates,
    });
    if (s.till) {
      const till = s.till;
      const [rates, items] = await Promise.all([
        ratesP,
        api().get<{ items: TerminalShiftSale[] }>(`/pos/terminal-shifts/${till.id}/sales`).then((r) => r.data.items).catch(() => null),
      ]);
      printHtml(buildShiftPrintHtml({
        cashier: till.openedByName,
        device: till.deviceName,
        openedAt: till.openedAt,
        closedAt: till.closedAt,
        salesCount: till.salesCount,
        shiftCurrency: till.currencyCode,
        breakdown: tillBreakdown(till),
        drawers: tillDrawerCards(till),
        note: till.note,
        rows: items ? tillSaleRows(items, till.currencyCode, t, shiftMoney) : null,
      }, t, opts(rates)));
      return;
    }
    if (!s.session) return;
    await printSessionCloseout(s.session, {
      t, money: shiftMoney, storeName: tenant?.name ?? '', locale: i18n.language, rtl: i18n.dir() === 'rtl',
      cashier: s.cashierName || null, device: s.deviceName || null,
    });
  };

  const filtered = rows.filter((r) => {
    if (sourceFilter !== 'all' && r.source !== sourceFilter) return false;
    if (branchFilter !== 'all') {
      if (branchFilter === 'none' ? r.warehouseId != null : r.warehouseId !== branchFilter) return false;
    }
    if (statusFilter === 'open' && !r.isOpen) return false;
    if (statusFilter === 'closed' && r.isOpen) return false;
    if (deviceFilter !== 'all' && r.deviceName !== deviceFilter) return false;
    if (cashierFilter !== 'all' && r.cashierName !== cashierFilter) return false;
    // A still-open shift is dated by when it opened — it has no close date yet.
    //
    // Taken from the LOCAL calendar day, not by slicing the ISO string. The
    // timestamp is UTC and the column beside this filter renders it in the
    // browser's zone, so in Baghdad (UTC+3) a shift closed at 01:20 shows as the
    // 11th and would have filtered as the 10th — an accountant asking for today
    // would lose rows visibly dated today.
    const day = localDay(r.closedAt ?? r.openedAt);
    if (fromDate && day < fromDate) return false;
    if (toDate && day > toDate) return false;
    // "Did not balance" means ANY currency in the drawer is off, not just the
    // register's own — a shift can be exact in IQD and short in USD.
    if (varianceOnly && !r.drawer.some((d) => d.variance !== null && Number(d.variance) !== 0)) return false;
    return true;
  });

  const columns: DataTableColumn<ShiftRow>[] = [
    {
      id: 'closedAt',
      header: t('shifts.closed', { defaultValue: 'Closed' }),
      sortValue: (s) => s.closedAt ?? s.openedAt,
      searchValue: (s) => `${s.deviceName} ${s.cashierName}`,
      cell: (s) => (
        <div className="min-w-0">
          <div className="font-medium">
            {s.closedAt
              ? formatDateTime(s.closedAt, { locale: i18n.language })
              : <Badge tone="info">{t('shifts.statusOpen', { defaultValue: 'Open' })}</Badge>}
          </div>
          <div className="truncate text-xs text-fg-muted">
            {t('shifts.opened', { defaultValue: 'Opened' })} {formatDateTime(s.openedAt, { locale: i18n.language })}
          </div>
        </div>
      ),
    },
    {
      id: 'device',
      header: t('shifts.device', { defaultValue: 'Terminal' }),
      sortValue: (s) => s.deviceName,
      cell: (s) => (
        <div className="min-w-0">
          <div className="truncate">{s.deviceName || '—'}</div>
          {s.cashierName ? <div className="truncate text-xs text-fg-muted">{s.cashierName}</div> : null}
        </div>
      ),
    },
    {
      id: 'source',
      header: t('shifts.source', { defaultValue: 'Source' }),
      sortValue: (s) => s.source,
      cell: (s) => (
        <Badge tone={s.source === 'terminal' ? 'neutral' : 'brand'}>
          {s.source === 'terminal'
            ? t('shifts.sourceTill', { defaultValue: 'Terminal app' })
            : t('shifts.sourceRegister', { defaultValue: 'Web register' })}
        </Badge>
      ),
    },
    {
      id: 'sales',
      header: t('shifts.sales', { defaultValue: 'Sales' }),
      align: 'center',
      sortValue: (s) => s.salesCount,
      cell: (s) => s.salesCount ?? '—',
    },
    {
      id: 'taken',
      header: t('shifts.taken', { defaultValue: 'Takings' }),
      align: 'end',
      cellClassName: 'font-mono tabular-nums',
      // Mixed-currency rows sort on their FIRST figure (the register's own
      // currency); there is no single number to rank a two-currency drawer by.
      sortValue: (s) => Number(s.takings[0]?.total ?? 0),
      cell: (s) => perCurrency(s.takings.map((k) => ({
        currencyCode: k.currencyCode,
        node: money(k.total, k.currencyCode),
      }))),
    },
    {
      // HOW the money came in, beside how much. A close-out that says only
      // "takings 400,000" cannot be reconciled against a drawer: the cash half
      // is what has to be in the till, the card half is what the bank owes.
      id: 'byMethod',
      header: t('shifts.byMethod', { defaultValue: 'By method' }),
      align: 'end',
      cellClassName: 'font-mono tabular-nums',
      sortValue: (s) => Number(s.breakdown[0]?.methods.find((m) => m.kind === 'cash')?.total ?? 0),
      cell: (s) => {
        // Zero methods are dropped rather than printed: a column of "0" for every
        // way the shop does not get paid is noise on the one number that matters.
        const blocks = s.breakdown.filter((c) => c.methods.length > 0 || c.payments.length > 0 || c.returns);
        if (blocks.length === 0) return <span className="text-fg-subtle">—</span>;
        const tag = (ccy: string): string => (blocks.length > 1 ? ` · ${ccy}` : '');
        return (
          <div className="flex flex-col items-end gap-0.5">
            {blocks.flatMap((c) => [
              ...c.methods.map((m) => (
                <span key={`${c.currencyCode}-${m.kind}`} className="whitespace-nowrap text-xs">
                  <span className="text-fg-subtle">{methodLabel(t, m.kind)}{tag(c.currencyCode)}: </span>
                  {money(m.total, c.currencyCode)}
                </span>
              )),
              ...(c.returns ? [(
                <span key={`${c.currencyCode}-ret`} className="whitespace-nowrap text-xs text-danger">
                  <span className="text-fg-subtle">{t('shifts.returnsRow', { defaultValue: 'Returns ({{n}})', n: c.returns.count })}{tag(c.currencyCode)}: </span>
                  − {money(c.returns.total, c.currencyCode)}
                </span>
              )] : []),
              ...c.payments.map((p) => (
                <span key={`${c.currencyCode}-pay-${p.method}`} className="whitespace-nowrap text-xs text-info">
                  <span className="text-fg-subtle">{t('shifts.debtPayments', { defaultValue: 'Debt payments' })} · {methodLabel(t, p.method)}{tag(c.currencyCode)}: </span>
                  {money(p.total, c.currencyCode)}
                </span>
              )),
            ])}
          </div>
        );
      },
    },
    {
      id: 'expected',
      header: t('shifts.expected', { defaultValue: 'Expected cash' }),
      align: 'end',
      cellClassName: 'font-mono tabular-nums text-fg-muted',
      sortValue: (s) => Number(s.drawer[0]?.expected ?? 0),
      cell: (s) => perCurrency(s.drawer.map((d) => ({
        currencyCode: d.currencyCode,
        node: money(d.expected, d.currencyCode),
      }))),
    },
    {
      id: 'counted',
      header: t('shifts.counted', { defaultValue: 'Counted' }),
      align: 'end',
      cellClassName: 'font-mono tabular-nums',
      sortValue: (s) => Number(s.drawer[0]?.counted ?? 0),
      cell: (s) => perCurrency(
        s.drawer
          .filter((d) => d.counted !== null)
          .map((d) => ({ currencyCode: d.currencyCode, node: money(d.counted ?? '0', d.currencyCode) })),
      ),
    },
    {
      // Print WITHOUT opening the row — a till slip or a register shift alike.
      // The slip already existed, one dialog deep, which is a click a manager
      // reconciling a stack of shifts makes fifty times.
      id: 'print',
      header: '',
      align: 'center',
      cell: (s) => (
        <Button
          variant="ghost"
          size="sm"
          title={tc('actions.print', { defaultValue: 'Print' })}
          aria-label={tc('actions.print', { defaultValue: 'Print' })}
          onClick={(e) => {
            e.stopPropagation(); // the row itself opens the dialog
            void printRow(s);
          }}
        >
          <Printer className="h-4 w-4" aria-hidden />
        </Button>
      ),
    },
    {
      id: 'difference',
      header: t('shifts.difference', { defaultValue: 'Difference' }),
      align: 'end',
      sortValue: (s) => Number(s.drawer[0]?.variance ?? 0),
      cell: (s) => {
        const counted = s.drawer.filter((d) => d.variance !== null);
        if (counted.length === 0) return '—';
        if (counted.every((d) => Number(d.variance) === 0)) {
          return <Badge tone="success">{t('shifts.balanced', { defaultValue: 'Balanced' })}</Badge>;
        }
        // A partly-off drawer still shows its exact currencies, so "0" beside a
        // short reads as "that half was counted and it was right".
        return perCurrency(counted.map((d) => {
          const v = Number(d.variance);
          return {
            currencyCode: d.currencyCode,
            node: (
              <span className={`font-mono tabular-nums font-semibold ${v === 0 ? 'text-fg-muted' : v < 0 ? 'text-danger' : 'text-warning'}`}>
                {v === 0 ? money('0', d.currencyCode) : `${v > 0 ? '+' : '−'}${money(String(Math.abs(v)), d.currencyCode)}`}
              </span>
            ),
          };
        }));
      },
    },
  ];

  const selectClass = 'h-9 rounded-md border border-border bg-bg px-2 text-sm text-fg outline-none focus:border-brand';

  return (
    <div className="flex flex-col gap-4">
      <header>
        <h1 className="text-2xl font-semibold">{t('shifts.title', { defaultValue: 'Register close-outs' })}</h1>
        <p className="mt-1 text-sm text-fg-muted">
          {t('shifts.description', {
            defaultValue: 'Every shift the cashier terminals and the web register opened and closed, with the drawer count they signed for.',
          })}
        </p>
      </header>

      {rows.length === 0 ? (
        <EmptyState
          icon={<Wallet className="h-8 w-8" aria-hidden />}
          title={t('shifts.empty', { defaultValue: 'No close-outs yet' })}
          description={t('shifts.emptyDescription', {
            defaultValue: 'A shift lands here when the cashier closes the register, on a terminal or in the browser. Offline shifts arrive on the next sync.',
          })}
        />
      ) : (
        <>
          <div className="flex flex-wrap items-end gap-2">
            <label className="flex flex-col gap-1">
              <span className="text-xs font-medium text-fg-muted">{tc('filters.from', { defaultValue: 'From' })}</span>
              <input type="date" value={fromDate} onChange={(e) => setFromDate(e.target.value)} className={selectClass} />
            </label>
            <label className="flex flex-col gap-1">
              <span className="text-xs font-medium text-fg-muted">{tc('filters.to', { defaultValue: 'To' })}</span>
              <input type="date" value={toDate} onChange={(e) => setToDate(e.target.value)} className={selectClass} />
            </label>
            {warehouses.length > 1 && (
              <label className="flex flex-col gap-1">
                <span className="text-xs font-medium text-fg-muted">{tc('filters.branch', { defaultValue: 'Branch' })}</span>
                <select value={branchFilter} onChange={(e) => setBranchFilter(e.target.value)} className={selectClass}>
                  <option value="all">{tc('filters.allBranches', { defaultValue: 'All branches' })}</option>
                  {warehouses.map((w) => <option key={w.id} value={w.id}>{w.name}</option>)}
                  <option value="none">{tc('filters.noBranch', { defaultValue: 'Unbranched' })}</option>
                </select>
              </label>
            )}
            <label className="flex flex-col gap-1">
              <span className="text-xs font-medium text-fg-muted">{t('shifts.device', { defaultValue: 'Terminal' })}</span>
              <select value={deviceFilter} onChange={(e) => setDeviceFilter(e.target.value)} className={selectClass}>
                <option value="all">{t('shifts.allTerminals', { defaultValue: 'All terminals' })}</option>
                {devices.map((d) => <option key={d} value={d}>{d}</option>)}
              </select>
            </label>
            <label className="flex flex-col gap-1">
              <span className="text-xs font-medium text-fg-muted">{t('shifts.cashier', { defaultValue: 'Cashier' })}</span>
              <select value={cashierFilter} onChange={(e) => setCashierFilter(e.target.value)} className={selectClass}>
                <option value="all">{t('shifts.allCashiers', { defaultValue: 'All cashiers' })}</option>
                {cashiers.map((c) => <option key={c} value={c}>{c}</option>)}
              </select>
            </label>
            <label className="flex flex-col gap-1">
              <span className="text-xs font-medium text-fg-muted">{t('shifts.status', { defaultValue: 'Status' })}</span>
              <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} className={selectClass}>
                <option value="all">{tc('filters.allStatuses', { defaultValue: 'All statuses' })}</option>
                <option value="open">{t('shifts.statusOpen', { defaultValue: 'Open' })}</option>
                <option value="closed">{t('shifts.statusClosed', { defaultValue: 'Closed' })}</option>
              </select>
            </label>
            <label className="flex flex-col gap-1">
              <span className="text-xs font-medium text-fg-muted">{t('shifts.source', { defaultValue: 'Source' })}</span>
              <select value={sourceFilter} onChange={(e) => setSourceFilter(e.target.value)} className={selectClass}>
                <option value="all">{t('shifts.allSources', { defaultValue: 'All sources' })}</option>
                <option value="terminal">{t('shifts.sourceTill', { defaultValue: 'Terminal app' })}</option>
                <option value="register">{t('shifts.sourceRegister', { defaultValue: 'Web register' })}</option>
              </select>
            </label>
            <label className="flex h-9 items-center gap-2 text-sm text-fg">
              <input
                type="checkbox"
                checked={varianceOnly}
                onChange={(e) => setVarianceOnly(e.target.checked)}
                className="h-4 w-4 rounded border-border accent-brand"
              />
              <span>{t('shifts.varianceOnly', { defaultValue: 'Out of balance only' })}</span>
            </label>
            {anyFilter && (
              <Button variant="ghost" size="sm" onClick={clearFilters}>
                {tc('filters.clear', { defaultValue: 'Clear' })}
              </Button>
            )}
          </div>

          <ShiftsTotals rows={filtered} />

          <Card className="overflow-hidden p-0">
            <DataTable
              columns={columns}
              data={filtered}
              rowKey={(s) => s.key}
              initialSort={{ id: 'closedAt', dir: 'desc' }}
              onRowClick={(s) => { if (s.till) setOpen(s.till); else if (s.session) setOpenSession(s.session); }}
            />
          </Card>
        </>
      )}

      <TerminalShiftDialog
        shift={open}
        onClose={() => setOpen(null)}
        onDeleted={(id) => {
          setTills((rs) => (rs ?? []).filter((r) => r.id !== id));
          setOpen(null);
        }}
      />
      <SessionSalesDialog session={openSession} onClose={() => setOpenSession(null)} />
    </div>
  );
}

/**
 * The totals of the shifts the filters show — per currency, how the money
 * came in (cash, card, transfer, on account…), the returns, the customers'
 * debt payments and the drawers' net over / short. "How much came in on cards
 * this week?" is a date filter and one glance.
 */
function ShiftsTotals({ rows }: { rows: ShiftRow[] }): JSX.Element | null {
  const { t, i18n } = useTranslation('pos');
  const money = useShiftMoney();
  const n = (v: number): string => formatNumber(v, { locale: i18n.language });
  if (rows.length === 0) return null;
  const totals = sumBreakdowns(rows.map((r) => r.breakdown), rows[0]?.till?.currencyCode ?? rows[0]?.session?.currencyCode ?? null);
  const variance = new Map<string, number>();
  for (const r of rows) {
    for (const d of r.drawer) {
      if (d.variance !== null) variance.set(d.currencyCode, (variance.get(d.currencyCode) ?? 0) + Number(d.variance));
    }
  }
  const blocks = totals.filter((c) => Number(c.takings) !== 0 || c.methods.length > 0 || c.payments.length > 0);
  if (blocks.length === 0) return null;
  return (
    <Card className="flex flex-col gap-3 p-4">
      <h2 className="text-sm font-semibold">
        {t('shifts.totalsTitle', { defaultValue: 'Totals for the {{n}} shifts shown', n: n(rows.length) })}
      </h2>
      <div className={`grid gap-3 ${blocks.length > 1 ? 'md:grid-cols-2' : ''}`}>
        {blocks.map((c) => {
          const v = variance.get(c.currencyCode) ?? 0;
          return (
            <div key={c.currencyCode} className="flex flex-col gap-1.5 rounded-xl border border-border bg-bg-subtle/40 p-3">
              <div className="flex items-baseline justify-between gap-2">
                <span className="text-sm font-semibold">{t('shifts.salesIn', { defaultValue: '{{currency}} sales', currency: c.currencyCode })}</span>
                <span dir="ltr" className="font-mono text-lg font-bold tabular-nums">{money(c.takings, c.currencyCode)}</span>
              </div>
              <div className="grid grid-cols-2 gap-x-4 gap-y-1 sm:grid-cols-3">
                {c.methods.map((m) => (
                  <div key={m.kind} className="flex flex-col rounded-lg bg-bg px-2.5 py-1.5">
                    <span className="text-[11px] text-fg-muted">
                      {methodLabel(t, m.kind)}{m.count !== null ? ` × ${n(m.count)}` : ''}
                    </span>
                    <span dir="ltr" className={`font-mono text-sm font-semibold tabular-nums ${m.kind === 'credit' ? 'text-warning' : ''}`}>
                      {money(m.total, c.currencyCode)}
                    </span>
                  </div>
                ))}
              </div>
              {Number(c.unsplit) > 0.004 && (
                <p className="text-xs text-fg-subtle">
                  {t('shifts.unsplit', { defaultValue: 'of which not split by method (older till): {{amount}}', amount: money(c.unsplit, c.currencyCode) })}
                </p>
              )}
              <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-fg-muted">
                {c.returns && (
                  <span className="flex items-center gap-1">
                    <Undo2 className="h-3.5 w-3.5 text-danger" aria-hidden />
                    {t('shifts.returnsRow', { defaultValue: 'Returns ({{n}})', n: n(c.returns.count) })}:{' '}
                    <span dir="ltr" className="font-mono font-semibold text-danger">− {money(c.returns.total, c.currencyCode)}</span>
                  </span>
                )}
                {c.payments.length > 0 && (
                  <span className="flex items-center gap-1">
                    <HandCoins className="h-3.5 w-3.5 text-info" aria-hidden />
                    {t('shifts.debtPayments', { defaultValue: 'Debt payments' })}:{' '}
                    <span dir="ltr" className="font-mono font-semibold text-info">
                      {money(c.payments.reduce((acc, p) => acc + Number(p.total), 0), c.currencyCode)}
                    </span>
                  </span>
                )}
                {Math.abs(v) > 0.004 && (
                  <span>
                    {t('shifts.netDifference', { defaultValue: 'Drawers over / short' })}:{' '}
                    <span dir="ltr" className={`font-mono font-semibold ${v < 0 ? 'text-danger' : 'text-warning'}`}>
                      {v > 0 ? '+' : '−'}{money(Math.abs(v), c.currencyCode)}
                    </span>
                  </span>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </Card>
  );
}
